import React from "react";
import SEO from "../../components/common/SEO";
import {
  Briefcase,
  Users,
  TrendingUp,
  Award,
  Zap,
  CheckCircle,
  ArrowRight,
  BarChart3,
  FileText,
  Sparkles,
} from "lucide-react";

export default function Agencies({ user, authToken }) {
  return (
    <>
      <SEO
        title="Email Validation for Agencies - VALIDMAIL"
        description="Deliver better client results with clean email lists. Pre-campaign validation, white-label reporting, and multi-client management for agencies."
        keywords="agency email validation, client email lists, white label validation, agency deliverability"
        canonicalUrl="https://validmail.io/solutions/agencies"
      />

      <div className="min-h-screen bg-white dark:bg-slate-950 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs sm:text-sm font-medium mb-4 sm:mb-6">
              <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
              For Agencies
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 dark:text-slate-50 mb-4 sm:mb-6">
              Deliver results that{" "}
              <span className="text-emerald-600 dark:text-emerald-400">win clients</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Clean client email lists, prove ROI with detailed reporting, and manage multiple accounts with ease. Built for agencies who deliver excellence.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-12 md:mb-16">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-emerald-200 dark:border-emerald-800 text-center">
              <Users className="h-8 w-8 sm:h-10 sm:w-10 text-emerald-600 dark:text-emerald-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">12+</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Clients Per Account</p>
            </div>

            <div className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-sky-200 dark:border-sky-800 text-center">
              <TrendingUp className="h-8 w-8 sm:h-10 sm:w-10 text-sky-600 dark:text-sky-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">+40%</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Campaign ROI</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-purple-200 dark:border-purple-800 text-center">
              <Award className="h-8 w-8 sm:h-10 sm:w-10 text-purple-600 dark:text-purple-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">98%</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Client Satisfaction</p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-amber-200 dark:border-amber-800 text-center">
              <BarChart3 className="h-8 w-8 sm:h-10 sm:w-10 text-amber-600 dark:text-amber-400 mx-auto mb-2 sm:mb-3" />
              <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-50">-62%</div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">Bounce Rate</p>
            </div>
          </div>

          <div className="mb-16 md:mb-24">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-50 text-center mb-8 sm:mb-12">
              How VALIDMAIL fits your agency
            </h2>
            <div className="relative">
              <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-emerald-200 via-emerald-400 to-emerald-200 dark:from-emerald-800 dark:via-emerald-600 dark:to-emerald-800 -translate-y-1/2"></div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-4 relative">
                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-emerald-200 dark:border-emerald-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    1
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Intake</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Receive client email lists and campaign requirements
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-emerald-200 dark:border-emerald-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    2
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Clean</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Validate and segment lists before launch
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-emerald-200 dark:border-emerald-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    3
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Launch</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Execute campaigns with verified, clean lists
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-emerald-200 dark:border-emerald-800 shadow-lg relative">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-lg sm:text-xl mb-3 sm:mb-4">
                    4
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-2 text-sm sm:text-base">Report</h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    Show results and email health improvements
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16 md:mb-24">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-50 text-center mb-8 sm:mb-12">
              Built for agency workflows
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-emerald-200 dark:border-emerald-800">
                <Briefcase className="h-10 w-10 sm:h-12 sm:w-12 text-emerald-600 dark:text-emerald-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Pre-Campaign List Cleaning
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Upload and validate client lists before every campaign. Remove risks and ensure maximum deliverability from day one.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Bulk upload CSV/Excel files up to 1000 emails</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Identify invalid, risky, and dangerous contacts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Export clean segments ready for import</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-900/20 dark:to-blue-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-sky-200 dark:border-sky-800">
                <FileText className="h-10 w-10 sm:h-12 sm:w-12 text-sky-600 dark:text-sky-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Client Dashboards & Reporting
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Show clients exactly what you're doing for them with detailed validation reports and email health scores.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-sky-600 dark:text-sky-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Detailed validation results with risk scoring</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-sky-600 dark:text-sky-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Before/after metrics to prove ROI</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-sky-600 dark:text-sky-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Exportable reports for client presentations</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-purple-200 dark:border-purple-800">
                <Users className="h-10 w-10 sm:h-12 sm:w-12 text-purple-600 dark:text-purple-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Multi-Client Management
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Manage validation for multiple clients from one account. Separate credits, reports, and workflows per client.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Organize validations by client project</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Track credit usage per client account</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Team access with role-based permissions</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl sm:rounded-2xl p-6 sm:p-8 border-2 border-amber-200 dark:border-amber-800">
                <Zap className="h-10 w-10 sm:h-12 sm:w-12 text-amber-600 dark:text-amber-400 mb-4" />
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-50 mb-3">
                  Fast Turnaround Times
                </h3>
                <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-4">
                  Process large client lists in seconds. No more waiting hours or days for validation results.
                </p>
                <ul className="space-y-2 text-xs sm:text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">1000 emails validated in under 30 seconds</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Meet tight campaign deadlines easily</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 dark:text-slate-300">Parallel processing for bulk uploads</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 rounded-2xl sm:rounded-3xl p-8 sm:p-12 border-2 border-slate-200 dark:border-slate-700 mb-16 md:mb-24">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs sm:text-sm font-medium mb-4">
                Case Study
              </div>
              <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-50 mb-4">
                Agency restores deliverability across 12 clients in 30 days
              </h3>
              <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 mb-6">
                A digital marketing agency managing email campaigns for 12 B2B clients was facing increasing bounce rates and declining campaign performance. After implementing VALIDMAIL's bulk validation workflow:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-600 dark:text-emerald-400 mb-1">62%</div>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Average bounce reduction</p>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-600 dark:text-emerald-400 mb-1">40%</div>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Improvement in campaign ROI</p>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-bold text-emerald-600 dark:text-emerald-400 mb-1">98%</div>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">Client satisfaction rate</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center text-white shadow-2xl">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Ready to deliver better results?
            </h2>
            <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 opacity-90 max-w-2xl mx-auto">
              Start validating client lists today. Volume discounts available for agencies.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={() => window.location.href = authToken ? "/product/bulk-validation" : "/signin"}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold bg-white text-emerald-600 hover:bg-slate-50 transition-all shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2"
              >
                {authToken ? "Start Validating" : "Get Started Free"}
                <ArrowRight className="h-5 w-5" />
              </button>
              <button
                onClick={() => window.location.href = "/pricing"}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-bold border-2 border-white text-white hover:bg-white hover:text-emerald-600 transition-all"
              >
                View Pricing
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
